import React, { useState, useMemo } from 'react';
import { Employee } from '../../types';
import Button from '../Form/Button';
import Input from '../Form/Input';
import Select from '../Form/Select';
import { useEmployees } from '../../hooks/useEmployees';
import { useBranches } from '../../hooks/useBranches';
import { useCrewChanges } from '../../hooks/useCrewChanges';
import { QuickCrewTable } from './QuickCrewListSections';
import { openCrewListPrintWindow } from '../../pages/CrewList/CrewListPrintTemplate';
import { generatePrintHtml } from '../../pages/ActiveCrewListArabic/ActiveCrewPrintTemplate';
import { arabicEmployeeName, englishEmployeeName, positionForLanguage } from '../../utils/employeeName';
import { resolveCrewStint } from '../../utils/crewStint';

export type QuickCrewLanguage = 'ar' | 'en';

interface VesselPrintInfo {
  vesselName: string;
  location: string;
  callSign: string;
  imoNumber: string;
  flag: string;
  date: string;
}

interface ExternalCrewMember {
  id: string;
  employeeId?: string;
  name: string;
  rank: string;
  nationality: string;
  dateOfBirth: string;
  seamanBookNo: string;
  seamanBookExpiry: string;
  dateOfJoin: string;
}

const emptyExternal: Omit<ExternalCrewMember, 'id'> = {
  name: '', rank: '', nationality: '', dateOfBirth: '',
  seamanBookNo: '', seamanBookExpiry: '', dateOfJoin: '',
};

interface QuickCrewListProps {
  onClose: () => void;
  /** 'ar' shows Arabic names, labels and an RTL Arabic print. Defaults to English. */
  language?: QuickCrewLanguage;
  /** Vessel details shown on the English print form (same block as the page print). */
  vesselInfo?: VesselPrintInfo;
}

const LABELS = {
  en: {
    title: 'Quick Active Crew List',
    subtitle: 'Generate and print crew list instantly without saving',
    close: 'Close',
    search: 'Search',
    searchPlaceholder: 'Search employees...',
    department: 'Department',
    allDepartments: 'All Departments',
    branch: 'Vessel/Headquarters',
    allBranches: 'All Vessels/HQ',
    selectAll: 'Select All',
    deselectAll: 'Deselect All',
    printSelected: (n: number) => `Print Selected (${n})`,
    selectedOf: (sel: number, total: number) => `${sel} of ${total} employees selected`,
    noResults: 'No employees found matching your criteria',
    addExternal: 'Add Person',
    addExternalHint: 'Pick an employee to auto-fill their data — or type manually. Print-only, not saved.',
    selectEmployee: 'Employee (optional)',
    selectEmployeePlaceholder: 'Choose an employee to auto-fill…',
    externalName: 'Name',
    externalRank: 'Rank',
    externalNationality: 'Nationality',
    externalDob: 'Date of Birth',
    externalSeaman: "Seaman's Book No.",
    externalSeamanExp: 'Book Expiry',
    externalJoin: 'Join Date',
    add: 'Add',
    externalAdded: (n: number) => `${n} extra ${n === 1 ? 'person' : 'people'} added`,
  },
  ar: {
    title: 'قائمة الطاقم النشط السريعة',
    subtitle: 'إنشاء وطباعة قائمة الطاقم فوراً بدون حفظ',
    close: 'إغلاق',
    search: 'بحث',
    searchPlaceholder: 'البحث في الموظفين...',
    department: 'القسم',
    allDepartments: 'جميع الأقسام',
    branch: 'السفينة/المقر',
    allBranches: 'جميع السفن/المقرات',
    selectAll: 'تحديد الكل',
    deselectAll: 'إلغاء تحديد الكل',
    printSelected: (n: number) => `طباعة المحدد (${n})`,
    selectedOf: (sel: number, total: number) => `تم تحديد ${sel} من ${total} موظف`,
    noResults: 'لا يوجد موظفون مطابقون لمعايير البحث',
    addExternal: 'إضافة شخص للقائمة',
    addExternalHint: 'اختر موظفاً لتعبئة بياناته تلقائياً — أو اكتب يدوياً. للطباعة فقط ولا يُحفظ.',
    selectEmployee: 'الموظف (اختياري)',
    selectEmployeePlaceholder: 'اختر موظفاً للتعبئة التلقائية…',
    externalName: 'الاسم',
    externalRank: 'الرتبة',
    externalNationality: 'الجنسية',
    externalDob: 'تاريخ الميلاد',
    externalSeaman: 'رقم الجواز البحري',
    externalSeamanExp: 'انتهاء الجواز',
    externalJoin: 'تاريخ الانضمام',
    add: 'إضافة',
    externalAdded: (n: number) => `تمت إضافة ${n} للطباعة`,
  },
};

const QuickCrewList: React.FC<QuickCrewListProps> = ({ onClose, language = 'en', vesselInfo }) => {
  const { data: apiEmployees = [] } = useEmployees({ populate: '*' });
  const { data: apiBranches = [] } = useBranches({ populate: '*' });
  const { data: crewChanges = [] } = useCrewChanges({ pagination: { pageSize: 1000 } });
  const t = LABELS[language];
  const nameFor = language === 'ar' ? arabicEmployeeName : englishEmployeeName;

  // Active crew = latest crew change per employee is Sign On/Continue
  // (same rule as the Active Crew List pages — the immigration flags are
  // paperwork state only and do not decide crew membership).
  const activeCrewByEmployee = useMemo(() => {
    const latest: Record<string, any> = {};
    (crewChanges as any[]).forEach((change) => {
      const key = String(change.employeeId ?? '');
      if (!key) return;
      const existing = latest[key];
      if (!existing || new Date(change.createdAt) > new Date(existing.createdAt)) latest[key] = change;
    });
    const map = new Map<string, any>();
    Object.values(latest).forEach((change: any) => {
      if (change.status === 'sign_on' || change.status === 'continue') {
        map.set(String(change.employeeId), change);
      }
    });
    return map;
  }, [crewChanges]);

  const crewChangesByEmployee = useMemo(() => {
    const map = new Map<string, any[]>();
    (crewChanges as any[]).forEach((change) => {
      const key = String(change.employeeId ?? '');
      if (!key) return;
      const list = map.get(key) || [];
      list.push(change);
      map.set(key, list);
    });
    return map;
  }, [crewChanges]);

  const activeChangeFor = (emp: Employee) =>
    activeCrewByEmployee.get(String((emp as any).documentId)) || activeCrewByEmployee.get(String(emp.id));

  // Join date = the sign_on that began the current stint, not a later `continue`.
  const joinDateFor = (emp: Employee) =>
    resolveCrewStint(
      crewChangesByEmployee.get(String((emp as any).documentId)) ||
      crewChangesByEmployee.get(String(emp.id)) || []
    ).joinDate;

  const employees = useMemo(() => {
    return (apiEmployees as Employee[]).filter(emp =>
      (emp.status === 'active' || (emp.status as string) === 'Active') &&
      activeChangeFor(emp) !== undefined
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiEmployees, activeCrewByEmployee]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [showExternalForm, setShowExternalForm] = useState(false);
  const [externalForm, setExternalForm] = useState({ ...emptyExternal });
  const [pickedEmployeeId, setPickedEmployeeId] = useState('');
  const [externalCrew, setExternalCrew] = useState<ExternalCrewMember[]>([]);

  // The extra person is usually one of our own employees who simply has no
  // Sign On crew change yet — offer every employee and auto-fill the fields.
  // Employees already ticked in the main list or already added are hidden.
  const employeePickOptions = useMemo(() => {
    const taken = new Set([
      ...selectedEmployees.map(String),
      ...externalCrew.map(e => e.employeeId).filter(Boolean) as string[],
    ]);
    return (apiEmployees as any[])
      .filter(e => !taken.has(String(e.documentId || e.id)))
      .map(e => ({
        value: String(e.documentId || e.id),
        label: `${e.name || '-'}${e.code ? ` (${e.code})` : ''}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [apiEmployees, selectedEmployees, externalCrew]);

  const handlePickEmployee = (employeeId: string) => {
    setPickedEmployeeId(employeeId);
    if (!employeeId) return;
    const emp = (apiEmployees as any[]).find(e => String(e.documentId || e.id) === employeeId);
    if (!emp) return;
    const dateOnly = (v?: string) => (v ? String(v).slice(0, 10) : '');
    setExternalForm({
      employeeId,
      name: nameFor(emp) || emp.name || '',
      rank: positionForLanguage(emp, language) || emp.rank || emp.position || '',
      nationality: emp.nationality || '',
      dateOfBirth: dateOnly(emp.birthDate),
      seamanBookNo: emp.seamanBookNumber || '',
      seamanBookExpiry: dateOnly(emp.seamanBookExpiry),
      dateOfJoin: new Date().toISOString().slice(0, 10),
    });
  };

  const filteredEmployees = useMemo(() => {
    let filtered = employees;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(employee =>
        (employee.name || '').toLowerCase().includes(term) ||
        ((employee as any).arabicName || '').toLowerCase().includes(term) ||
        (employee.code || '').toLowerCase().includes(term) ||
        (employee.position || '').toLowerCase().includes(term)
      );
    }

    if (selectedDepartment) {
      filtered = filtered.filter(employee => employee.department === selectedDepartment);
    }

    if (selectedBranch) {
      // The vessel comes from the active crew change; the employee's own
      // branch is only a fallback for old records.
      filtered = filtered.filter(employee => {
        const change = activeChangeFor(employee);
        const branchId = change?.branchId || employee.branchId;
        return String(branchId) === String(selectedBranch);
      });
    }

    return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, searchTerm, selectedDepartment, selectedBranch, activeCrewByEmployee]);

  const departmentOptions = useMemo(() => {
    const uniqueDepts = new Set(employees.map((e: any) => e.department).filter(Boolean));
    return [
      { value: '', label: t.allDepartments },
      ...Array.from(uniqueDepts).sort().map(d => ({ value: d as string, label: d as string }))
    ];
  }, [employees, t.allDepartments]);

  const branchOptions = useMemo(() => {
    const options = [{ value: '', label: t.allBranches }];
    if (apiBranches && apiBranches.length > 0) {
      apiBranches.forEach((branch: any) => {
        options.push({
          value: branch.documentId || branch.id?.toString() || '',
          label: branch.name || 'Unknown'
        });
      });
    }
    return options;
  }, [apiBranches, t.allBranches]);

  const handleEmployeeToggle = (employeeId: string) => {
    setSelectedEmployees(prev => 
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const handleSelectAll = () => {
    if (selectedEmployees.length === filteredEmployees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(filteredEmployees.map(emp => emp.id));
    }
  };

  const handleAddExternal = () => {
    if (!externalForm.name.trim()) return;
    setExternalCrew(prev => [...prev, { ...externalForm, id: `ext_${Date.now()}` }]);
    setExternalForm({ ...emptyExternal });
    setPickedEmployeeId('');
  };

  const handleRemoveExternal = (id: string) => {
    setExternalCrew(prev => prev.filter(e => e.id !== id));
  };

  const handlePrint = () => {
    const selectedEmployeesData = employees.filter(emp => selectedEmployees.includes(emp.id));
    const locale = language === 'ar' ? 'ar-EG' : undefined;
    const fmt = (v?: string) => (v ? new Date(v).toLocaleDateString(locale) : '-');

    // Same rows and same template as the page's regular print so the output
    // is identical — external people are appended as extra rows only.
    const crewRows = [
      ...selectedEmployeesData.map(emp => ({
        name: nameFor(emp) || '-',
        rank: positionForLanguage(emp, language) || '-',
        nationality: emp.nationality || '-',
        dateOfBirth: fmt(emp.birthDate),
        seamanBookNo: emp.seamanBookNumber || '-',
        seamanBookExpiry: fmt(emp.seamanBookExpiry),
        dateOfJoin: fmt(joinDateFor(emp) || undefined),
      })),
      ...externalCrew.map(e => ({
        name: e.name || '-',
        rank: e.rank || '-',
        nationality: e.nationality || '-',
        dateOfBirth: e.dateOfBirth ? fmt(e.dateOfBirth) : '-',
        seamanBookNo: e.seamanBookNo || '-',
        seamanBookExpiry: e.seamanBookExpiry ? fmt(e.seamanBookExpiry) : '-',
        dateOfJoin: e.dateOfJoin ? fmt(e.dateOfJoin) : '-',
      })),
    ];

    if (language === 'ar') {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(generatePrintHtml(crewRows));
        printWindow.document.close();
      }
    } else {
      openCrewListPrintWindow(vesselInfo || {
        vesselName: '...............', location: '...............',
        callSign: '...............', imoNumber: '...............',
        flag: '...............', date: new Date().toLocaleDateString(),
      }, crewRows);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto" dir={language === 'ar' ? 'rtl' : 'ltr'}>
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">
              {t.title}
            </h2>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t.close}
            </Button>
          </div>
          <p className="text-gray-600 mt-2">
            {t.subtitle}
          </p>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Input
              label={t.search}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t.searchPlaceholder}
            />
            <Select
              label={t.department}
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              options={departmentOptions}
            />
            <Select
              label={t.branch}
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              options={branchOptions}
            />
          </div>

          <div className="flex flex-wrap gap-4 items-center mb-6">
            <Button type="button" variant="secondary" onClick={handleSelectAll}>
              {selectedEmployees.length === filteredEmployees.length ? t.deselectAll : t.selectAll}
            </Button>

            <Button type="button" variant="secondary" onClick={() => setShowExternalForm(v => !v)}>
              {t.addExternal}
            </Button>

            {(selectedEmployees.length > 0 || externalCrew.length > 0) && (
              <Button type="button" variant="primary" onClick={handlePrint}>
                {t.printSelected(selectedEmployees.length + externalCrew.length)}
              </Button>
            )}

            <div className="text-sm text-gray-600">
              {t.selectedOf(selectedEmployees.length, filteredEmployees.length)}
              {externalCrew.length > 0 && ` — ${t.externalAdded(externalCrew.length)}`}
            </div>
          </div>

          {showExternalForm && (
            <div className="border border-dashed border-gray-300 rounded-lg p-4 mb-6">
              <p className="text-xs text-gray-500 mb-3">{t.addExternalHint}</p>
              <div className="mb-3">
                <Select
                  label={t.selectEmployee}
                  value={pickedEmployeeId}
                  onChange={(e) => handlePickEmployee(e.target.value)}
                  options={[{ value: '', label: t.selectEmployeePlaceholder }, ...employeePickOptions]}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <Input label={t.externalName} value={externalForm.name}
                  onChange={(e) => setExternalForm(f => ({ ...f, name: e.target.value }))} />
                <Input label={t.externalRank} value={externalForm.rank}
                  onChange={(e) => setExternalForm(f => ({ ...f, rank: e.target.value }))} />
                <Input label={t.externalNationality} value={externalForm.nationality}
                  onChange={(e) => setExternalForm(f => ({ ...f, nationality: e.target.value }))} />
                <Input label={t.externalDob} type="date" value={externalForm.dateOfBirth}
                  onChange={(e) => setExternalForm(f => ({ ...f, dateOfBirth: e.target.value }))} />
                <Input label={t.externalSeaman} value={externalForm.seamanBookNo}
                  onChange={(e) => setExternalForm(f => ({ ...f, seamanBookNo: e.target.value }))} />
                <Input label={t.externalSeamanExp} type="date" value={externalForm.seamanBookExpiry}
                  onChange={(e) => setExternalForm(f => ({ ...f, seamanBookExpiry: e.target.value }))} />
                <Input label={t.externalJoin} type="date" value={externalForm.dateOfJoin}
                  onChange={(e) => setExternalForm(f => ({ ...f, dateOfJoin: e.target.value }))} />
                <div className="flex items-end">
                  <Button type="button" variant="primary" onClick={handleAddExternal} disabled={!externalForm.name.trim()}>
                    {t.add}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {externalCrew.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {externalCrew.map(e => (
                <span key={e.id} className="inline-flex items-center gap-2 bg-gray-100 border border-gray-300 rounded-full px-3 py-1 text-sm text-gray-800">
                  {e.name}{e.rank ? ` — ${e.rank}` : ''}
                  <button type="button" onClick={() => handleRemoveExternal(e.id)}
                    className="text-gray-500 hover:text-red-600 font-bold leading-none" aria-label="Remove">×</button>
                </span>
              ))}
            </div>
          )}

          <QuickCrewTable
            filteredEmployees={filteredEmployees}
            selectedEmployees={selectedEmployees}
            onToggle={handleEmployeeToggle}
            onSelectAll={handleSelectAll}
            language={language}
            nameFor={nameFor}
          />

          {filteredEmployees.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {t.noResults}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickCrewList;
